'use client'

import type { User } from '@/lib/types'
import { useState } from 'react'
import { DeviceToggle } from './device-toggle'
import { PreviewIframe } from './preview-iframe'
import { TemplateActions } from './template-actions'
import { TemplateTitle } from './template-title'

type TTemplatePreviewSectionProps = {
  previewUrl: string
  templateTitle: string
  templateId: string
  userId?: string
  user: User | null
  initialDeviceView?: 'desktop' | 'mobile'
}

/**
 * Wrapper component managing device toggle and preview iframe with shared state
 * @param previewUrl - URL to preview in iframe
 * @param templateTitle - Title of the template
 * @param templateId - Template ID
 * @param userId - User ID for analytics
 * @param user - Current user object
 * @param initialDeviceView - Initial device view
 * @returns Complete preview section with title, toggle, actions, and iframe
 */
export function TemplatePreviewSection({
  previewUrl,
  templateTitle,
  templateId,
  userId,
  user,
  initialDeviceView = 'desktop',
}: TTemplatePreviewSectionProps) {
  const [deviceView, setDeviceView] = useState<'desktop' | 'mobile'>(
    initialDeviceView,
  )

  return (
    <>
      {/* Template Title, Device Toggle, and Actions */}
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
        {/* Title - full width on mobile, auto on desktop */}
        <div className="w-full md:w-auto">
          <TemplateTitle title={templateTitle} />
        </div>

        {/* Device toggle and actions - stacked row on mobile */}
        <div className="flex flex-col-reverse sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <DeviceToggle
            userId={userId}
            onDeviceChange={setDeviceView}
            initialDevice={initialDeviceView}
          />
          <TemplateActions
            templateId={templateId}
            templateTitle={templateTitle}
            previewUrl={previewUrl}
            user={user}
          />
        </div>
      </div>

      {/* Template Preview Iframe */}
      <PreviewIframe
        previewUrl={previewUrl}
        templateTitle={templateTitle}
        deviceView={deviceView}
      />
    </>
  )
}
